// Atribución de los leads que llegan por WhatsApp. Puro (lo usan el sitio y el servidor).
//
// El formulario web ya envía la atribución de primer contacto (attribution.ts), pero la mayoría de
// los leads del sitio entra por los botones de WhatsApp: el mensaje llega a la API de WhatsApp sin
// página ni canal. Al hacer clic en un enlace a wa.me, el sitio agrega al texto prellenado una
// referencia corta («(Ref: org /anfitrionas)») y, al crear el lead desde la conversación, el CRM la
// lee del primer mensaje. Si la persona la borra, el lead queda sin dato, como antes.

import { CANALES, type Attribution, type Canal } from "./attribution";

/** Código corto de cada canal en la referencia (legible y difícil de confundir con texto libre). */
const CODIGO: Record<Canal, string> = {
  organico: "org",
  pago: "ads",
  social: "rs",
  email: "mail",
  referido: "ref",
  directo: "dir",
};
const CANAL_DE_CODIGO = new Map(CANALES.map((c) => [CODIGO[c], c]));

const RUTA_MAX = 120;
/** «(Ref: org /anfitrionas)»; la ruta es la de aterrizaje del primer contacto. */
const REFERENCIA = /\(Ref: ([a-z]+) (\/[^\s)]{0,120})\)/;

/**
 * Query con `encodeURIComponent` (espacios como %20). `URLSearchParams.toString()` los escribe como
 * «+», y WhatsApp no siempre los decodifica: en algunos clientes el mensaje prellenado se veía
 * «Hola,+quiero+cotizar». Es la misma codificación de `whatsappHref` (lib/site.ts).
 */
function conQuery(url: URL): string {
  const query = [...url.searchParams]
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  return `${url.origin}${url.pathname}${query ? `?${query}` : ""}${url.hash}`;
}

const rutaSegura = (ruta: string | null): string => {
  const limpia = (ruta ?? "/").split(/[?#]/)[0].replace(/[\s()]/g, "");
  return (limpia.startsWith("/") ? limpia : "/").slice(0, RUTA_MAX);
};

/**
 * Agrega la referencia al texto prellenado de un enlace de WhatsApp. Devuelve el href sin tocar si
 * no es de wa.me o si ya trae una referencia (dos clics no la duplican).
 */
export function agregarReferenciaWhatsapp(
  href: string,
  atribucion: Pick<Attribution, "canal" | "landing_page">,
): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  if (url.hostname !== "wa.me" && url.hostname !== "api.whatsapp.com") return href;
  const texto = url.searchParams.get("text") ?? "";
  if (REFERENCIA.test(texto)) return href;
  const ref = `(Ref: ${CODIGO[atribucion.canal]} ${rutaSegura(atribucion.landing_page)})`;
  url.searchParams.set("text", texto ? `${texto}\n\n${ref}` : ref);
  return conQuery(url);
}

/** Lee la referencia de un mensaje de WhatsApp; null si no la trae o es inválida. */
export function leerReferenciaWhatsapp(
  texto: string | null | undefined,
): { canal: Canal; landing_page: string } | null {
  const m = (texto ?? "").match(REFERENCIA);
  if (!m) return null;
  const canal = CANAL_DE_CODIGO.get(m[1]);
  return canal ? { canal, landing_page: m[2] } : null;
}
